import { ArrowLeft } from 'lucide-react';
import { useEffect } from 'react';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { useActionsNote, useBaseNote, useNote, type NoteSaisie } from '@/api/hooks';
import { LIBELLE_NATURE_CHANGEMENT, type LigneBudgetNote, type NatureChangement } from '@/api/types';
import { Chargement, ErreurBloc, PageHeader, StatutBadge } from '@/components/common';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { cn, montant } from '@/lib/utils';

const NATURES = Object.keys(LIBELLE_NATURE_CHANGEMENT) as NatureChangement[];
const NATURE_COUT: Record<string, string> = { PERDIEM: 'Perdiem', LOGEMENT: 'Logement', HEBERGEMENT: 'Hébergement', TRANSPORT: 'Transport', PROTOCOLE: 'Protocole', REPRESENTATION: 'Représentation', FRAIS_TERMINAUX: 'Frais terminaux' };

interface Valeurs {
  objet: string;
  natures: NatureChangement[];
  motifs: string;
  impactBudgetaire: boolean;
  budget: LigneBudgetNote[];
}

/** Note explicative : nouvelle (depuis un TDR soumis ou autorisé) ou en correction. */
export function NoteFormulaire() {
  const { tdrId: tdrParam, id } = useParams();
  const navigate = useNavigate();
  const existante = useNote(id);
  const tdrId = tdrParam ?? existante.data?.note.tdrId;
  const base = useBaseNote(tdrId);
  const actions = useActionsNote();
  const form = useForm<Valeurs>({ defaultValues: { objet: '', natures: [], motifs: '', impactBudgetaire: false, budget: [] } });
  const budget = useFieldArray({ control: form.control, name: 'budget' });
  const valeurs = useWatch({ control: form.control });

  // Budget de départ : celui du TDR (nouveau = ancien), ou celui déjà saisi dans la note en correction.
  useEffect(() => {
    if (existante.data) {
      const n = existante.data.note;
      form.reset({ objet: n.objet, natures: n.natures, motifs: n.motifs, impactBudgetaire: n.impactBudgetaire, budget: n.budget?.length ? n.budget : (base.data?.budget ?? []) });
    } else if (base.data && form.getValues('budget').length === 0) {
      form.setValue('budget', base.data.budget);
    }
  }, [existante.data, base.data, form]);

  if ((id && existante.isLoading) || base.isLoading) return <Chargement />;
  if (existante.error || base.error) return <ErreurBloc erreur={existante.error ?? base.error} />;
  const tdr = base.data?.tdr ?? existante.data?.tdr;
  const feds = base.data?.feds ?? existante.data?.feds ?? [];
  const lignes = valeurs.budget ?? [];
  const ancien = lignes.reduce((s, l) => s + (Number(l?.ancien) || 0), 0);
  const nouveau = lignes.reduce((s, l) => s + (Number(l?.nouveau) || 0), 0);
  const ecart = Math.round((nouveau - ancien) * 100) / 100;
  const motifsLongueur = (valeurs.motifs ?? '').trim().length;

  const enregistrer = form.handleSubmit(
    async (v) => {
      const corps: NoteSaisie = { tdrId: tdrId!, objet: v.objet.trim(), natures: v.natures, motifs: v.motifs.trim(), impactBudgetaire: v.impactBudgetaire, budget: v.impactBudgetaire ? v.budget.map((l) => ({ ...l, ancien: Number(l.ancien) || 0, nouveau: Number(l.nouveau) || 0 })) : undefined };
      try {
        const r = id ? await actions.modifier.mutateAsync({ id, corps }) : await actions.creer.mutateAsync(corps);
        toast.success(id ? 'Note modifiée.' : 'Note enregistrée en brouillon.');
        navigate(`/ne/${r.note.id}`);
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Enregistrement impossible.');
      }
    },
    () => toast.error("Renseignez l'objet (10 caractères), au moins une nature et l'exposé des motifs (100 caractères)."),
  );

  return (
    <form onSubmit={enregistrer} className="space-y-6">
      <PageHeader
        retour={
          <Link to={id ? `/ne/${id}` : `/tdr/${tdrId}`} className="mb-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="size-4" /> Retour
          </Link>
        }
        titre={id ? `Corriger la note ${existante.data?.note.numero ?? ''}` : 'Nouvelle note explicative'}
        sousTitre={tdr ? <>Sur le TDR {tdr.numero} · {tdr.titre} · budget actuel {montant(tdr.montant, true)}. La note suit le circuit complet de validation.</> : undefined}
        actions={
          <Button type="submit" disabled={actions.creer.isPending || actions.modifier.isPending}>
            {id ? 'Enregistrer les corrections' : 'Enregistrer le brouillon'}
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Objet et motifs</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="objet">Objet de la note</Label>
            <Input id="objet" {...form.register('objet', { required: true, minLength: 10 })} placeholder="Par exemple : prolongation de deux jours de la mission de Bunia" />
          </div>
          <div className="space-y-1.5">
            <Label>Nature des changements</Label>
            <Controller
              control={form.control}
              name="natures"
              rules={{ validate: (v) => v.length > 0 }}
              render={({ field }) => (
                <div className="flex flex-wrap gap-3">
                  {NATURES.map((n) => (
                    <label key={n} className="flex items-center gap-2 text-sm">
                      <Checkbox checked={field.value.includes(n)} onCheckedChange={(c) => field.onChange(c === true ? [...field.value, n] : field.value.filter((x) => x !== n))} />
                      {LIBELLE_NATURE_CHANGEMENT[n]}
                    </label>
                  ))}
                </div>
              )}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="motifs">
              Exposé des motifs <span className={cn('text-xs', motifsLongueur < 100 ? 'text-amber-700' : 'text-muted-foreground')}>({motifsLongueur} / 100 caractères au moins)</span>
            </Label>
            <Textarea id="motifs" rows={6} {...form.register('motifs', { required: true, minLength: 100 })} placeholder="Faits, raisons et conséquences du changement." />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Controller control={form.control} name="impactBudgetaire" render={({ field }) => <Checkbox checked={field.value} onCheckedChange={(c) => field.onChange(c === true)} />} />
            Incidence budgétaire (le budget du TDR change)
          </label>
        </CardContent>
      </Card>

      {valeurs.impactBudgetaire && (
        <Card>
          <CardHeader>
            <CardTitle>
              Budget révisé{' '}
              <span className={cn('num float-right', ecart > 0 ? 'text-amber-700' : ecart < 0 ? 'text-emerald-700' : '')}>
                {ecart >= 0 ? '+' : ''}
                {montant(ecart, true)}
              </span>
            </CardTitle>
            <CardDescription>
              Ancien budget {montant(ancien, true)} → nouveau {montant(nouveau, true)}
              {base.data ? ` · disponible prévisionnel de la ligne : ${montant(base.data.disponible)}` : ''}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Bloc</TableHead>
                  <TableHead>Nature</TableHead>
                  <TableHead className="text-right">Ancien</TableHead>
                  <TableHead className="w-40 text-right">Nouveau</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {budget.fields.map((f, i) => (
                  <TableRow key={f.id}>
                    <TableCell>{f.bloc}</TableCell>
                    <TableCell>{NATURE_COUT[f.nature] ?? f.nature}</TableCell>
                    <TableCell className="num text-right">{montant(f.ancien, true)}</TableCell>
                    <TableCell>
                      <Input className="text-right" type="number" min={0} step="0.01" {...form.register(`budget.${i}.nouveau`, { valueAsNumber: true })} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {ecart > 0 && base.data && ecart > base.data.disponible && (
              <Alert variant="destructive" className="mt-3">
                <AlertTitle>Écart supérieur au disponible prévisionnel</AlertTitle>
                <AlertDescription>La soumission sera refusée tant que l'écart n'est pas couvert.</AlertDescription>
              </Alert>
            )}
            {feds.length > 0 && (
              <Alert className="mt-3">
                <AlertTitle>FED liés au TDR</AlertTitle>
                <AlertDescription>
                  <div className="flex flex-wrap gap-2">
                    {feds.map((f) => (
                      <span key={f.id} className="inline-flex items-center gap-1">
                        <Link to={`/fed/${f.id}`} className="text-primary hover:underline">
                          {f.numero ?? 'FED (brouillon)'}
                        </Link>
                        <StatutBadge statut={f.statut} />
                        {f.suspendu && <Badge variant="destructive">suspendu</Badge>}
                      </span>
                    ))}
                  </div>
                  Avec incidence budgétaire, ces FED sont suspendus pendant le circuit de la note.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" asChild>
          <Link to={id ? `/ne/${id}` : `/tdr/${tdrId}`}>Abandonner</Link>
        </Button>
        <Button type="submit" disabled={actions.creer.isPending || actions.modifier.isPending}>
          {id ? 'Enregistrer les corrections' : 'Enregistrer le brouillon'}
        </Button>
      </div>
    </form>
  );
}
